/**
 * Scheduler — builds a daily practice session from the user's card states.
 *
 * Responsibilities:
 * 1. Track which curriculum nodes are unlocked (prerequisites met).
 * 2. Select which items the user practices in any given session.
 * 3. Determine when a node is "mastered" so downstream nodes unlock.
 *
 * The scheduler is pure: it takes (cards, items, nodes, focus, time, config) and
 * returns a session plan. Persistence is the caller's responsibility.
 */

import { type Card, type FsrsParams, type Rating, isDue, currentRetrievability, newCard, review } from "./fsrs.js";
import { type CurriculumNode, type Item, MVP_NODES } from "./item-library.js";

/** Map from item ID to its FSRS card. The user's review history. */
export type CardStore = Record<string, Card>;

export interface SessionConfig {
  /** Target session length in minutes. The scheduler picks ~6 items/min. */
  targetMinutes: number;
  /** Mix ratios. */
  mix: {
    /** Fraction reserved for items currently due (review). */
    review: number;
    /** Fraction reserved for new items from upcoming nodes. */
    new: number;
  };
  /** Items per minute estimate used to convert minutes → item count. */
  itemsPerMinute: number;
}

export const DEFAULT_SESSION_CONFIG: SessionConfig = {
  targetMinutes: 20,
  mix: { review: 0.4, new: 0.6 },
  itemsPerMinute: 6,
};

export interface SessionPlan {
  /** Items to practice this session, in suggested order. */
  items: Item[];
  /** Diagnostic info for UI display. */
  breakdown: {
    review: number;
    new: number;
  };
  /** Total estimated minutes. */
  estimatedMinutes: number;
  /** Which scheduling bucket each item ID came from. */
  itemCategories: Record<string, "review" | "new">;
  /**
   * Node IDs being introduced for the first time in this session —
   * items from these nodes appear in the new bucket AND none of their
   * items have ever been reviewed before. Used to show "Introducing: X"
   * banners in the UI.
   */
  newlyIntroducedNodeIds: string[];
}

export interface MasteryState {
  /** Items in this node that have reached mastery thresholds. */
  masteredCount: number;
  /** Items in this node that are at "practicing" tier. */
  practicingCount: number;
  /** Items the user has touched at least once but that haven't yet
   *  reached the practicing threshold. Distinct from untouched items
   *  (which contribute to neither the introduced count nor the bar). */
  introducedCount: number;
  /** Total items in the node. */
  totalCount: number;
  /** "introduced" | "practicing" | "mastered" */
  tier: "locked" | "available" | "introduced" | "practicing" | "mastered";
}

/** Stability-in-days thresholds that define the mastery tiers. */
export interface MasteryThresholds {
  /** Stability (days) required to reach the "mastered" tier. */
  mastered: number;
  /** Stability (days) required to reach the "practicing" tier. */
  practicing: number;
}

/** Named presets for the mastery thresholds preference. */
export const MASTERY_PRESETS = {
  standard:    { mastered: 30, practicing: 7 },
  intensive:   { mastered: 14, practicing: 5 },
  accelerated: { mastered: 7,  practicing: 3 },
} satisfies Record<string, MasteryThresholds>;

export type MasteryPreset = keyof typeof MASTERY_PRESETS;

/**
 * Mastery thresholds (per the curriculum spec):
 *   introduced — at least one review
 *   practicing — stability ≥ thresholds.practicing AND no lapses AND ≥3 reps
 *   mastered   — stability ≥ thresholds.mastered
 */
export function itemTier(
  card: Card | undefined,
  thresholds: MasteryThresholds = MASTERY_PRESETS.standard,
): "new" | "introduced" | "practicing" | "mastered" {
  if (!card || card.state === "new") return "new";
  if (card.stability >= thresholds.mastered) return "mastered";
  if (card.stability >= thresholds.practicing && card.lapses === 0 && card.reps >= 3) return "practicing";
  return "introduced";
}

export function nodeMastery(
  node: CurriculumNode,
  items: Item[],
  cards: CardStore,
  thresholds: MasteryThresholds = MASTERY_PRESETS.standard,
): MasteryState {
  const nodeItems = items.filter((i) => i.nodeId === node.id);
  let mastered = 0;
  let practicing = 0;
  let introduced = 0;
  for (const item of nodeItems) {
    const tier = itemTier(cards[item.id], thresholds);
    if (tier === "mastered") mastered++;
    else if (tier === "practicing") practicing++;
    else if (tier === "introduced") introduced++;
  }
  let tier: MasteryState["tier"] = "available";
  if (mastered === nodeItems.length) tier = "mastered";
  else if (practicing + mastered >= Math.ceil(nodeItems.length * 0.75)) tier = "practicing";
  else if (introduced + practicing + mastered > 0) tier = "introduced";
  return {
    masteredCount: mastered,
    practicingCount: practicing,
    introducedCount: introduced,
    totalCount: nodeItems.length,
    tier,
  };
}

/**
 * Phase 2A reachability predicate — is this node available for practice?
 *
 * Phase 1 gated practice behind a prereq cascade (`unlockedNodes`): the user
 * had to bring upstream nodes to "practicing" tier before downstream nodes
 * unlocked. Phase 2A flips that default — every node is reachable from day
 * one. The placement test (Block A.3) is what gives the user a meaningful
 * starting state; the prereq graph remains in `CurriculumNode.prerequisites`
 * for future use (suggested learning order, FSRS placement weighting), but
 * it no longer gates what the user can drill.
 *
 * Returns `true` for every node by default. Pass `opts.requirePrereqs: true`
 * to opt back into the Phase 1 prereq-cascade behavior (used by the
 * informational `unlockedNodes` display and by tests that need to verify
 * prereq math directly).
 */
export function isNodeReachable(
  node: CurriculumNode,
  items: Item[],
  cards: CardStore,
  opts?: { requirePrereqs?: boolean; thresholds?: MasteryThresholds },
): boolean {
  if (!opts?.requirePrereqs) return true;
  if (node.prerequisites.length === 0) return true;
  return node.prerequisites.every((prereqId) => {
    // Mastery for the prereq node, computed against its own items.
    const prereqItems = items.filter((i) => i.nodeId === prereqId);
    if (prereqItems.length === 0) return false;
    // Build a tiny synthetic node so we can reuse nodeMastery without
    // importing the full prereq-node graph here.
    const synth: CurriculumNode = {
      id: prereqId,
      track: node.track,
      title: prereqId,
      phase: node.phase,
      prerequisites: [],
    };
    const m = nodeMastery(synth, prereqItems, cards, opts.thresholds);
    return m.tier === "practicing" || m.tier === "mastered";
  });
}

/**
 * Determine which nodes meet the prereq cascade.
 *
 * Phase 1 used this as the practice gate. Phase 2A keeps it for *informational*
 * use — e.g., the Skill Tree may want to surface "this is the recommended
 * next step" markers without locking anything. The scheduler itself no longer
 * filters items through this; see `isNodeReachable`.
 *
 * `opts.forceAll` short-circuits the check and returns every node verbatim.
 * Preserved for the MVP "Unlock all curriculum" testing toggle, though it's
 * effectively redundant in Phase 2A since reachability is true for all by
 * default.
 */
export function unlockedNodes(
  nodes: CurriculumNode[],
  items: Item[],
  cards: CardStore,
  opts?: { forceAll?: boolean; thresholds?: MasteryThresholds },
): CurriculumNode[] {
  if (opts?.forceAll) return [...nodes];
  const masteryByNode = new Map<string, MasteryState>();
  for (const n of nodes) masteryByNode.set(n.id, nodeMastery(n, items, cards, opts?.thresholds));
  return nodes.filter((node) => {
    if (node.prerequisites.length === 0) return true;
    return node.prerequisites.every((prereqId) => {
      const m = masteryByNode.get(prereqId);
      if (!m) return false;
      return m.tier === "practicing" || m.tier === "mastered";
    });
  });
}

/**
 * Returns curriculum nodes that are currently eligible for new-item
 * introduction: every prerequisite has been introduced (≥1 item reviewed)
 * AND the node itself still has unseen items. Safe to call frequently —
 * pure function over the current card state.
 */
export function getEligibleNewNodes(
  nodes: CurriculumNode[],
  items: Item[],
  cards: CardStore,
): CurriculumNode[] {
  const introduced = getIntroducedNodes(nodes, items, cards);
  const eligibleIds = new Set(
    nodes.filter((n) => n.prerequisites.every((p) => introduced.has(p))).map((n) => n.id),
  );
  return nodes.filter(
    (n) =>
      eligibleIds.has(n.id) &&
      items.some((i) => i.nodeId === n.id && (!cards[i.id] || cards[i.id]!.state === "new")),
  );
}

/** Build a session plan for today. The core scheduler entry point. */
export function buildSession(
  args: {
    items: Item[];
    cards: CardStore;
    nodes?: CurriculumNode[];
    config?: SessionConfig;
    now?: Date;
    /**
     * Vestigial in Phase 2A — every node is reachable by default, so this
     * flag is now a no-op. Kept on the API for source-compatibility with
     * call sites that still pass it. Will be removed in a later pass.
     */
    forceAllUnlocked?: boolean;
    /**
     * When set, restricts the new-item pool to this specific node only.
     * Falls back to the full eligible frontier if the node has no remaining
     * unseen items (e.g. all items are already introduced or mastered).
     * Null / undefined = auto, draw from the full eligible frontier.
     */
    newSkillNodeId?: string | null;
    /** Mastery thresholds — which stability values count as practicing/mastered. */
    thresholds?: MasteryThresholds;
    /**
     * Overrides the new-item shuffle seed. Omitted → the seed is derived from
     * the UTC day (so previews and refreshes within a day are stable). Session
     * starts pass a fresh per-session value so a new session doesn't reproduce
     * the previous one's item order on the same day.
     */
    seed?: number;
  },
): SessionPlan {
  const nodes = args.nodes ?? MVP_NODES;
  const config = args.config ?? DEFAULT_SESSION_CONFIG;
  const now = args.now ?? new Date();
  const totalCount = Math.round(config.targetMinutes * config.itemsPerMinute);
  const reviewQuota = Math.round(totalCount * config.mix.review);
  const newQuota = totalCount - reviewQuota;

  // Phase 2A: every node is reachable by default. The prereq cascade no
  // longer gates the practice schedule. See `isNodeReachable` for the
  // rationale.
  const reachableIds = new Set(
    nodes.filter((n) => isNodeReachable(n, args.items, args.cards)).map((n) => n.id),
  );
  const availableItems = args.items.filter((i) => reachableIds.has(i.nodeId));

  // 1. Review items: due cards, prioritized by lowest current retrievability
  //    (most-likely-to-be-forgotten first).
  const dueItems = availableItems
    .filter((i) => {
      const c = args.cards[i.id];
      return c && isDue(c, now) && c.state !== "new";
    })
    .map((i) => ({
      item: i,
      r: currentRetrievability(args.cards[i.id]!, now),
    }))
    .sort((a, b) => a.r - b.r)
    .map((x) => x.item);

  const reviewItems = dueItems.slice(0, reviewQuota);

  // 2. New items: drawn from the pedagogically eligible frontier — only nodes
  //    whose prerequisites have been at least introduced. This prevents random
  //    items from advanced nodes appearing before foundational ones are touched.
  const introducedNodes = getIntroducedNodes(nodes, availableItems, args.cards);
  const newPool = eligibleNewPool(nodes, availableItems, args.cards, introducedNodes);
  // Always introduce items from exactly ONE node per session so the user
  // isn't simultaneously thrown into multiple unfamiliar skills.
  //   • Explicit pick: use args.newSkillNodeId (falls back to full pool if exhausted).
  //   • Auto: pick the first node in curriculum order that has eligible unseen items.
  const singleNodeNewPool = (() => {
    if (args.newSkillNodeId) {
      const constrained = newPool.filter((i) => i.nodeId === args.newSkillNodeId);
      return constrained.length > 0 ? constrained : newPool;
    }
    const firstNodeId = nodes.find((n) => newPool.some((i) => i.nodeId === n.id))?.id;
    return firstNodeId ? newPool.filter((i) => i.nodeId === firstNodeId) : newPool;
  })();
  const newItems = pickRandom(singleNodeNewPool, newQuota, now, args.seed);

  // Build per-item category map before dedup (first assignment wins if overlap).
  const itemCategories: Record<string, "review" | "new"> = {};
  for (const item of reviewItems) itemCategories[item.id] = "review";
  for (const item of newItems) if (!itemCategories[item.id]) itemCategories[item.id] = "new";

  // Avoid duplicates if any item somehow appears in multiple buckets.
  const seen = new Set<string>();
  const finalItems: Item[] = [];
  for (const list of [reviewItems, newItems]) {
    for (const item of list) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        finalItems.push(item);
      }
    }
  }

  // Interleave for pedagogical variety — avoid 10 reviews in a row, then 10 focus.
  const interleaved = interleave(finalItems);

  // Nodes appearing in newItems that the user has never touched before.
  const newlyIntroducedNodeIds = [
    ...new Set(newItems.map((i) => i.nodeId)),
  ].filter((nodeId) => !introducedNodes.has(nodeId));

  return {
    items: interleaved,
    breakdown: {
      review: reviewItems.length,
      new: newItems.length,
    },
    estimatedMinutes: Math.round(interleaved.length / config.itemsPerMinute),
    itemCategories,
    newlyIntroducedNodeIds,
  };
}

/**
 * Returns how many non-new cards are due on each of the next `horizonDays` days.
 * Index 0 = today, 1 = tomorrow, etc. Cards that are already overdue bucket
 * into index 0 (today). New cards (state: "new") are excluded.
 */
export function forecastDue(
  cards: CardStore,
  horizonDays: number,
  now: Date = new Date(),
): number[] {
  const counts = new Array<number>(horizonDays).fill(0);
  const todayStart = localMidnight(now);
  for (const card of Object.values(cards)) {
    if (card.state === "new") continue;
    const dueDay = Math.floor(
      (localMidnight(new Date(card.due)).getTime() - todayStart.getTime()) / 86_400_000,
    );
    const idx = Math.max(0, dueDay); // overdue → today
    if (idx < horizonDays) counts[idx]!++;
  }
  return counts;
}

/**
 * Returns the `n` items with the lowest current retrievability among reviewed
 * (non-new) cards — the skills the user is most likely to have forgotten.
 */
export function weakestItems(
  items: Item[],
  cards: CardStore,
  n: number,
  now: Date = new Date(),
): Item[] {
  return items
    .filter((item) => {
      const card = cards[item.id];
      return card !== undefined && card.state !== "new";
    })
    .map((item) => ({
      item,
      r: currentRetrievability(cards[item.id]!, now),
    }))
    .sort((a, b) => a.r - b.r)
    .slice(0, n)
    .map((x) => x.item);
}

/** Apply a review result to the card store, returning a new store. */
export function applyReview(
  cards: CardStore,
  itemId: string,
  rating: Rating,
  now: Date = new Date(),
  params?: FsrsParams,
): CardStore {
  const existing = cards[itemId] ?? newCard(now);
  const updated = review(existing, rating, now, params);
  return { ...cards, [itemId]: updated };
}

/* ─── helpers ─── */

/** Returns midnight at the start of the given date in local time. */
function localMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Returns the set of node IDs that have been "introduced" — at least one
 * item from each has been reviewed at least once (card.state !== "new").
 */
function getIntroducedNodes(nodes: CurriculumNode[], items: Item[], cards: CardStore): Set<string> {
  const introduced = new Set<string>();
  for (const node of nodes) {
    const nodeItems = items.filter((i) => i.nodeId === node.id);
    if (nodeItems.some((i) => { const c = cards[i.id]; return c !== undefined && c.state !== "new"; })) {
      introduced.add(node.id);
    }
  }
  return introduced;
}

/**
 * Restricts the new-item pool to nodes whose prerequisites have all been
 * introduced. This enforces the pedagogical ordering encoded in the
 * prerequisite graph: the user won't see items from, say, triad-quality
 * until they've at least touched diatonic-intervals-major.
 *
 * A node is eligible when every prerequisite node ID is in `introducedNodes`.
 * Nodes with no prerequisites (e.g. diatonic-degrees-major) are always
 * eligible. Falls back to the full unseen pool if the eligible set is empty
 * (defensive; shouldn't happen with a normal curriculum).
 */
function eligibleNewPool(
  nodes: CurriculumNode[],
  items: Item[],
  cards: CardStore,
  introducedNodes: Set<string>,
): Item[] {
  const eligibleNodeIds = new Set(
    nodes
      .filter((n) => n.prerequisites.every((p) => introducedNodes.has(p)))
      .map((n) => n.id),
  );
  const pool = items.filter(
    (i) => eligibleNodeIds.has(i.nodeId) && (!cards[i.id] || cards[i.id]!.state === "new"),
  );
  // Fallback: guard against a degenerate state where the eligible pool is empty.
  return pool.length > 0
    ? pool
    : items.filter((i) => !cards[i.id] || cards[i.id]!.state === "new");
}

/** Deterministic pseudo-random pick using an explicit or date-derived seed. */
function pickRandom<T>(arr: T[], n: number, seedDate: Date, seedOverride?: number): T[] {
  // Default seed is day-of-year so previews/refreshes within a day are consistent
  // but vary across days. Callers (session starts) pass an explicit seed so a
  // fresh session doesn't reproduce the previous session's order the same day.
  // Always shuffle — even when the pool fits in n — so callers don't see
  // pool-order leak through (e.g. scale-degree items in literal degree order).
  const seed =
    seedOverride ?? seedDate.getUTCFullYear() * 1000 + Math.floor(seedDate.getTime() / 86_400_000);
  const indexed = arr.map((item, i) => ({ item, key: hash(seed + i) }));
  indexed.sort((a, b) => a.key - b.key);
  return indexed.slice(0, n).map((x) => x.item);
}

function hash(n: number): number {
  let x = n | 0;
  x = ((x >> 16) ^ x) * 0x45d9f3b;
  x = ((x >> 16) ^ x) * 0x45d9f3b;
  x = (x >> 16) ^ x;
  return x >>> 0;
}

/** Round-robin interleave to avoid runs of similar items. */
function interleave<T>(items: T[]): T[] {
  if (items.length <= 4) return items;
  // Split into 3 buckets and zip them. Simple but effective.
  const buckets: T[][] = [[], [], []];
  items.forEach((item, i) => buckets[i % 3]!.push(item));
  const out: T[] = [];
  let remaining = true;
  while (remaining) {
    remaining = false;
    for (const b of buckets) {
      if (b.length > 0) {
        out.push(b.shift()!);
        remaining = true;
      }
    }
  }
  return out;
}
